import { useState } from 'react'
import { parse } from 'papaparse'
import { Icon } from '@iconify/react'
import { pluralize } from '@pepper/utils/src/function/pluralize'
import {
  Button,
  DraggerUploader,
  Spin,
  useMessage,
  useCreateBulkTopics,
} from '@pepper/ui'
import { BatchTopic } from '@pepper/utils/src/types/seo'

import { downloadSample, getBulkImportMsg } from '../../../utils'

import s from './Uploader.module.css'

const sampleRecord = [
  {
    topic: 'topic',
    primary_keyword: 'primary keyword',
    secondary_keywords: 'secondary keyword (separated by comma)',
  },
]

type Props = {
  companyId: string
  projectId: string
  onClose: () => void
  setIsAddingBulkTopics: (value: boolean) => void
}

export const Uploader: React.FC<Props> = ({
  onClose,
  companyId,
  projectId,
  setIsAddingBulkTopics,
}) => {
  const [isUploadError, setIsUploadError] = useState(false)
  const [topics, setTopics] = useState<BatchTopic[]>([])
  const { showError, showSuccess, showWarning } = useMessage()

  const { mutateAsync: addBulkTopics, isLoading: isBulkAdditionLoading } =
    useCreateBulkTopics()

  const handleAddBulkTopics = async () => {
    setIsAddingBulkTopics(true)
    const response = await addBulkTopics({
      companyId,
      projectId,
      data: topics,
    })
    const records = response?.records
    const newRecordIds = response?.new_record_ids
    setIsAddingBulkTopics(false)
    if (response?.status === 'success' && !!records?.length) {
      const totalAdded = newRecordIds?.length ?? 0
      const totalSkipped = topics.length - totalAdded
      const msg = getBulkImportMsg({
        type: 'Topic',
        totalAdded,
        totalSkipped,
      })

      if (totalAdded === 0) {
        showWarning(msg)
      } else {
        showSuccess(msg)
      }
      setTopics([])
      onClose()
    } else {
      showError(response?.description)
    }
  }

  const formatPayload = (value: { data: string[] }) => {
    if (value.data[0][0].toUpperCase() !== 'TOPIC') {
      setIsUploadError(true)
      return
    }
    const payload = value.data
      .filter((_, index) => index > 0)
      .map(topicItem => {
        const topic = topicItem[0]
        const primaryKeyword = topicItem[1]?.trim()
        const secondaryKeywords = (topicItem[2] ?? '')
          .split(',')
          .map(keyword => keyword?.trim())
          .filter(keyword => keyword?.length > 0)

        return {
          topic: topic?.length > 0 ? topic : undefined,
          primary_keyword:
            primaryKeyword?.length > 0 ? primaryKeyword : undefined,
          secondary_keywords:
            secondaryKeywords?.length > 0 ? secondaryKeywords : undefined,
        }
      })
    const trimmedRequestPayload = payload.filter(
      topicItem => topicItem.topic
    ) as BatchTopic[]
    if (trimmedRequestPayload.length > 0) {
      setTopics(trimmedRequestPayload)
    } else {
      setIsUploadError(true)
    }
  }

  const handleFileUpload = (file?: {
    originFileObj: NodeJS.ReadableStream
  }) => {
    setIsUploadError(false)
    setTopics([])
    if (!file) return
    parse(file.originFileObj, {
      complete: formatPayload,
      error: () => {
        setIsUploadError(true)
      },
    })
  }

  return (
    <Spin spinning={isBulkAdditionLoading}>
      <div className={s.body}>
        <div className={s.description}>
          <DraggerUploader
            showUploadStatusToast={false}
            onFileRemove={() => setTopics([])}
            showList={!isUploadError}
            handleFiles={handleFileUpload}
            allowedFiles={['.csv']}
            caption="Click to Upload"
            className={s.link}
            info={
              <p>
                <span>Upload</span>
                <span className={s.fileType}>&nbsp;.csv file (12MB max)</span>
              </p>
            }
          />
        </div>
        <div className={s.suggestion}>
          <span>The rows in the file should be in a particular order.</span>
          <button
            className={s.link}
            onClick={() =>
              downloadSample({
                sampleRecord,
                sampleFileName: 'topics-sample.csv',
              })
            }
          >
            Download Sample File
          </button>
        </div>
        {isUploadError && (
          <>
            <div className={s.errorInfo}>
              File upload was unsuccessful. Please resolve following issues and
              upload again:
            </div>
            <div className={s.error}>
              <Icon icon="lucide:info" width="14" height="14" /> File
              doesn&apos;t match the format
            </div>
            <div className={s.error}>
              <Icon icon="lucide:info" width="14" height="14" /> You cannot
              leave mandatory fields blank
            </div>
          </>
        )}
      </div>
      <div className={s.footer}>
        <Button type="link" htmlType="button" onClick={onClose}>
          Cancel
        </Button>
        <Button
          type="primary"
          onClick={handleAddBulkTopics}
          loading={isBulkAdditionLoading}
          disabled={topics.length === 0}
        >
          {topics.length > 0 ? (
            <span>Add {pluralize(topics.length, 'Topic')}</span>
          ) : (
            <span>Add Topics</span>
          )}
        </Button>
      </div>
    </Spin>
  )
}
